import { Injectable } from '@angular/core';
import { IAnnualReport } from '@modeso/types__aumico-lib-annualreport-be';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import Debug from 'debug';
import { of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, switchMap } from 'rxjs/operators';
import { setAnnualReportMetaInfo } from '../../../../aumico-lib-annualreport-fe/src/lib/actions/annualReport.actions';
import { AumicoCoreProvider, NotificationType } from '../../../../aumico-lib-core-fe/src/lib';
import { ProjectsOverviewActions } from '../actions/projects-overview.actions';
import { AnnualReportDataDto } from '../dtos/annual-report-data-dto.interface';
import { ProjectMetaInformationDto } from '../dtos/project-meta-information-dto.interface';
import { ProjectsService } from '../services/projects.service';
const debug = Debug('aumico:projects:projectsOverviewEffects');

@Injectable()
export class projectsOverviewEffects {
    constructor(
        private actions$: Actions,
        private projectsService: ProjectsService,
        private coreProvider: AumicoCoreProvider,
    ) { }

    // Shows the success message in the singular when exactly one project was deleted.
    private notifyProjectsDeleted(deletedCount: number): void {
        if (deletedCount === 1) {
            this.coreProvider.dispatchAddNotification(
                '',
                'aumico_project_overview_project_delete_success_message',
                NotificationType.SUCCESS,
            );
            return;
        }
        this.coreProvider.dispatchAddNotification(
            'aumico_cpmm_projects_bulk_delete_success_title',
            'aumico_cpmm_projects_bulk_delete_success_description',
            NotificationType.SUCCESS,
        );
    }

    loadProjectsOverview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.loadProjectsOverview),
            debounceTime(100), // Add debounce to ensure only the last trigger within 100ms is processed
            switchMap((action) => {
                debug('refetch projects overview with filter:', action.filter);
                return this.projectsService.getProjectsMetaInformation(action.filter).pipe(
                    map((response: { projectsMetaInformation: ProjectMetaInformationDto[]; totalCount: number }) =>
                        ProjectsOverviewActions.projectsOverviewLoaded({ response }),
                    ),
                    catchError((error) => of(ProjectsOverviewActions.projectsOverviewLoadFailure({ error }))),
                );
            }),
        ),
    );

    fetchProjectsList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.fetchProjectsList),
            switchMap((action) => {
                return this.projectsService.getProjectsMetaInformation(action.filter).pipe(
                    map((response: { projectsMetaInformation: ProjectMetaInformationDto[]; totalCount: number }) =>
                        ProjectsOverviewActions.projectsListFetched({ response }),
                    ),
                    catchError((error) => of(ProjectsOverviewActions.projectsListFetchFailure({ error }))),
                );
            }),
        ),
    );

    loadMoreProjects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.loadMoreProjects),
            mergeMap((action) => {
                return this.projectsService.getProjectsMetaInformation(action.filter).pipe(
                    map((response: { projectsMetaInformation: ProjectMetaInformationDto[]; totalCount: number }) =>
                        ProjectsOverviewActions.moreProjectsLoaded({ response }),
                    ),
                    catchError((error) => of(ProjectsOverviewActions.moreProjectsLoadFailure({ error }))),
                );
            }),
        ),
    );

    updateProjectStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.updateProjectStatus),
            mergeMap((action) => {
                return this.projectsService
                    .updateProjectMetaInformationStatus(
                        action.projectId,
                        action.status,
                        action.updateTasks,
                        action.mandateManagementTaskIds,
                        action.closingSectionIds,
                    )
                    .pipe(
                        map((response: ProjectMetaInformationDto) =>
                            ProjectsOverviewActions.projectStatusUpdated({ response }),
                        ),
                        catchError((error) => of(ProjectsOverviewActions.projectStatusUpdateFailure({ error }))),
                    );
            }),
        ),
    );

    bulkUpdateProjectStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.bulkUpdateProjectStatus),
            mergeMap((action) =>
                this.projectsService
                    .bulkUpdateProjectStatus(
                        action.projectIds,
                        action.status,
                        action.updateTasks,
                        action.mandateManagementTaskIds,
                        action.closingSectionIds,
                    )
                    .pipe(
                        map((response) => {
                            this.coreProvider.dispatchAddNotification(
                                'aumico_cpmm_project_status_bulk_update_success_title',
                                'aumico_cpmm_project_status_bulk_update_success_description',
                                NotificationType.SUCCESS,
                            );
                            return ProjectsOverviewActions.bulkProjectStatusUpdated({ response });
                        }),
                        catchError((error) => {
                            this.coreProvider.dispatchAddNotification(
                                'aumico_cpmm_project_status_bulk_update_failure_title',
                                'aumico_cpmm_project_status_bulk_update_failure_description',
                                NotificationType.ERROR,
                            );
                            return of(ProjectsOverviewActions.bulkUpdateProjectStatusFailure({ error }));
                        }),
                    ),
            ),
        ),
    );

    deleteProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.deleteProject),
            mergeMap((action) => {
                return this.projectsService.deleteProjectMetaInformation(action.projectId).pipe(
                    switchMap(() => [ProjectsOverviewActions.projectDeleted({ response: action.projectId })]),
                    catchError((error) => of(ProjectsOverviewActions.projectDeleteFailure({ error, projectId: action.projectId }))),
                );
            }),
        ),
    );

    bulkDeleteProjects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.bulkDeleteProjects),
            mergeMap((action) => {
                return this.projectsService.bulkDeleteProjects(action.projectIds).pipe(
                    switchMap((response) => {
                        const deletedIds = Array.isArray(response.deletedIds) ? response.deletedIds : [];
                        const warnings = Array.isArray(response.warnings) ? response.warnings : [];

                        if (deletedIds.length > 0 && warnings.length === 0) {
                            this.notifyProjectsDeleted(deletedIds.length);
                        }
                        return [
                            ProjectsOverviewActions.bulkProjectsDeleted({
                                deletedIds,
                                warnings,
                            }),
                        ];
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_projects_bulk_delete_failure_title',
                            'aumico_cpmm_projects_bulk_delete_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(ProjectsOverviewActions.bulkProjectsDeleteFailure({ error }));
                    }),
                );
            }),
        ),
    );

    confirmBulkDeleteWithDependencies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.confirmBulkDeleteWithDependencies),
            mergeMap((action) => {
                return this.projectsService.bulkDeleteProjects(action.projectIds, action.projectIds).pipe(
                    switchMap((response) => {
                        const deletedIds = Array.isArray(response.deletedIds) ? response.deletedIds : [];

                        if (deletedIds.length > 0) {
                            this.notifyProjectsDeleted(deletedIds.length);
                        }
                        return [
                            ProjectsOverviewActions.bulkProjectsDeleted({
                                deletedIds,
                                warnings: [],
                            }),
                        ];
                    }),
                    catchError((error) => {
                        this.coreProvider.dispatchAddNotification(
                            'aumico_cpmm_projects_bulk_delete_failure_title',
                            'aumico_cpmm_projects_bulk_delete_failure_description',
                            NotificationType.ERROR,
                        );
                        return of(ProjectsOverviewActions.bulkProjectsDeleteFailure({ error }));
                    }),
                );
            }),
        ),
    );

    fetchProjectIdByAnnualReportMetaInfoId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.fetchProjectIdByAnnualReportMetaInfoId),
            mergeMap((action) => {
                return this.projectsService.fetchProjectIdByAnnualReportMetaInfoId(action.annualReportMetaId).pipe(
                    map((projectId: string | null) => {
                        return ProjectsOverviewActions.projectIdByAnnualReportMetaInfoIdFetched({ projectId });
                    }),
                    catchError((error) => {
                        return of(ProjectsOverviewActions.projectIdByAnnualReportMetaInfoIdFetchFailure({ error }));
                    }),
                );
            }),
        ),
    );

    fetchProjectIdByAnnualReportId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProjectsOverviewActions.fetchProjectDataByProjectId),
            mergeMap((action) => {
                return this.projectsService.getAllProjectsInformationByProjectId(action.projectId).pipe(
                    switchMap((data: AnnualReportDataDto) => {
                        const actionsToDispatch = [];

                        actionsToDispatch.push(
                            ProjectsOverviewActions.projectDataByProjectIdFetched({
                                projectId: data.projectId,
                                projectMetaData: data.projectMetaData,
                                annualReportMetaInfo: data.annualReportMetaInfo || null,
                            }),
                        );

                        if (data.annualReportMetaInfo && data.annualReport) {
                            const annualReport: IAnnualReport = {
                                ...data.annualReport,
                                dataCenter: undefined,
                            };
                            actionsToDispatch.push(
                                setAnnualReportMetaInfo({
                                    annualReportMetaInfo: data.annualReportMetaInfo,
                                    annualReport: annualReport || null,
                                }),
                            );
                        }

                        return actionsToDispatch;
                    }),
                    catchError((error) => {
                        return of(ProjectsOverviewActions.projectDataByProjectIdFetchFailure({ error }));
                    }),
                );
            }),
        ),
    );
}
